import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import {
  fetchSupportTickets,
  SUPPORT_TICKETS_DEFAULT_PAGE_SIZE,
} from "../apis/fetchSupportTickets";
import { searchSupportTickets } from "../apis/searchSupportTickets";
import type {
  SupportTicketApiItem,
  SupportTicketListEntry,
  SupportTicketPageApiResponse,
} from "../supportTicketTypes";
import { mapSupportTicketListEntry } from "../utils/mapSupportTicketApiData";

/**
 * Return value for {@link useSupportTickets}.
 */
export interface UseSupportTicketsReturn {
  tickets: SupportTicketListEntry[];
  selectedTicketId: string;
  loading: boolean;
  searching: boolean;
  error: string | null;
  /** Whether the sidebar is showing archived tickets (`is_archived: true`). */
  isArchiveView: boolean;
  setSelectedTicketId: (ticketId: string) => void;
  searchTickets: (query: string) => Promise<void>;
  refetchTickets: (options?: RefetchSupportTicketsOptions) => Promise<void>;
  /** Toggles between the active and archived ticket lists. */
  toggleArchiveView: () => void;
}

/**
 * Options for refreshing the support ticket list without a full-page loader.
 */
export interface RefetchSupportTicketsOptions {
  /** Ticket id to select after the refresh completes. */
  selectTicketId?: string;
  /** When true, skips the initial full-page loading state. */
  silent?: boolean;
}

/**
 * Applies ticket rows to state and selects the first matching ticket by default.
 *
 * @param nextTickets - Ticket rows returned from the list or search API.
 * @param selectTicketId - Optional ticket id to select after the update.
 * @returns The id selected after applying the ticket rows.
 */
function resolveSelectedTicketId(
  nextTickets: SupportTicketApiItem[],
  selectTicketId?: string,
): string {
  if (
    selectTicketId &&
    nextTickets.some((ticket) => ticket.id === selectTicketId)
  ) {
    return selectTicketId;
  }

  return nextTickets[0]?.id ?? "";
}

/**
 * Loads tickets for the current archive mode and optional search query.
 *
 * Uses get-tickets (or search when a query is present) with `is_archived`
 * matching the archive-icon toggle state.
 *
 * @param orgId - Organization/facility id.
 * @param isArchiveView - When true, fetch archived tickets only.
 * @param query - Optional sidebar search text.
 * @returns Paginated ticket response for the current view.
 */
async function loadSupportTicketsForView(
  orgId: string,
  isArchiveView: boolean,
  query?: string,
): Promise<SupportTicketPageApiResponse> {
  const trimmedQuery = query?.trim() ?? "";

  if (trimmedQuery) {
    return searchSupportTickets({
      org_id: orgId,
      q: trimmedQuery,
      is_archived: isArchiveView,
      page: 1,
      limit: SUPPORT_TICKETS_DEFAULT_PAGE_SIZE,
    });
  }

  return fetchSupportTickets({
    org_id: orgId,
    is_archived: isArchiveView,
    page: 1,
    limit: SUPPORT_TICKETS_DEFAULT_PAGE_SIZE,
  });
}

/**
 * Loads support tickets on mount and exposes sidebar/detail state for the page.
 *
 * @returns Ticket list, selection state, loading/error flags, and search handler.
 */
export function useSupportTickets(): UseSupportTicketsReturn {
  const [apiTickets, setApiTickets] = useState<SupportTicketApiItem[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isArchiveView, setIsArchiveView] = useState(false);
  const searchQueryRef = useRef("");
  const isArchiveViewRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  const applyTicketResults = useCallback(
    (nextTickets: SupportTicketApiItem[], selectTicketId?: string) => {
      setApiTickets(nextTickets);
      setSelectedTicketId(resolveSelectedTicketId(nextTickets, selectTicketId));
    },
    [],
  );

  const fetchTicketsForCurrentView = useCallback(
    async (
      options?: RefetchSupportTicketsOptions & { useSearchLoader?: boolean },
    ) => {
      const orgId = getOrgIdForFetchExternalWorklist();
      if (!orgId) {
        setApiTickets([]);
        setSelectedTicketId("");
        setError("Organization id is unavailable.");
        setLoading(false);
        setSearching(false);
        return;
      }

      if (options?.useSearchLoader) {
        setSearching(true);
      } else if (!options?.silent) {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await loadSupportTicketsForView(
          orgId,
          isArchiveViewRef.current,
          searchQueryRef.current,
        );
        applyTicketResults(response.data ?? [], options?.selectTicketId);
      } catch (fetchError) {
        setApiTickets([]);
        setSelectedTicketId("");
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : "Failed to load support tickets.",
        );
      } finally {
        if (options?.useSearchLoader) {
          setSearching(false);
        } else if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    [applyTicketResults],
  );

  const refetchTickets = useCallback(
    async (options?: RefetchSupportTicketsOptions) => {
      await fetchTicketsForCurrentView(options);
    },
    [fetchTicketsForCurrentView],
  );

  const searchTickets = useCallback(
    async (query: string) => {
      searchQueryRef.current = query;
      setSelectedTicketId("");
      await fetchTicketsForCurrentView({ useSearchLoader: true });
    },
    [fetchTicketsForCurrentView],
  );

  /**
   * Switches between the active and archived ticket lists.
   */
  const toggleArchiveView = useCallback(() => {
    setIsArchiveView((prev) => {
      const next = !prev;
      isArchiveViewRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      void fetchTicketsForCurrentView();
      return;
    }

    void fetchTicketsForCurrentView({ useSearchLoader: true });
  }, [fetchTicketsForCurrentView, isArchiveView]);

  const tickets = useMemo(
    () => apiTickets.map(mapSupportTicketListEntry),
    [apiTickets],
  );

  return {
    tickets,
    selectedTicketId,
    loading,
    searching,
    error,
    isArchiveView,
    setSelectedTicketId,
    searchTickets,
    refetchTickets,
    toggleArchiveView,
  };
}
